import React, { useState, useEffect, useCallback, useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { ModalPortal } from '../components/modals/ModalPortal';
import { ModalReports } from '../components/modals/ModalReports';
import { SelectFileModal as ModalSelect } from '../components/modals/SelectFileModal';
import { ActionsModal } from '../components/shared/ActionsModal';
import { ReactComponent as ClipboardReportsIcon } from '../react-icons/clipboardReports.svg';
import { ReactComponent as DownloadFileIcon } from '../react-icons/downloadFile.svg';
import { ReactComponent as PlusIcon } from '../react-icons/plusIcon.svg';
import { ReactComponent as SailTrackLogo } from '../react-icons/SailTrackLogo.svg';
import { ReactComponent as SearchIcon } from '../react-icons/searchIcon.svg';
import { ReactComponent as SelectCSVFileIcon } from '../react-icons/selectCSVFile.svg';
import { ReactComponent as SortReportsIcon } from '../react-icons/sortReports.svg';
import { ReactComponent as TrashIcon } from '../react-icons/trashIcon.svg';
import { ReactComponent as UploadFileIcon } from '../react-icons/uploadFile.svg';
import { showMessage } from '../showMessage'; // Asegúrate de que esta función esté disponible
import { type SharedUser, type SharingMetadata } from '../types';
import '../styles/ReportsPage.css';

import type { ModalAction } from '../components/shared/ActionsModal';

const API_BASE_URL: string = process.env.REACT_APP_API_BASE_URL ?? '';
const K_NOTIF_ERROR = 'notifications.error' as const;
const K_R_BACKEND = 'reports.backendUrlMissing' as const;
const K_R_AUTH = 'reports.authError' as const;
const K_R_FETCH_FAIL = 'reports.fetchFail' as const;
const K_R_NO_FILEID = 'reports.noFileId' as const;
const K_R_INVALID_FILE = 'reports.invalidFileId' as const;
const K_R_INVALID_EVENT = 'reports.invalidEventId' as const;
const K_R_DOWNLOAD_FAIL = 'reports.downloadFail' as const;
const K_R_DOWNLOAD_FILE_FAIL = 'reports.downloadFileFail' as const;
const SORT_DATE_ASC = 'Date Asc' as const;
const SORT_DATE_DESC = 'Date Desc' as const;
const SORT_EVENT_AZ = 'Event A-Z' as const;
const SORT_EVENT_ZA = 'Event Z-A' as const;
const SORT_BOAT_AZ = 'Boat A-Z' as const;
const SORT_BOAT_ZA = 'Boat Z-A' as const;
const K_R_FULL_FILE = 'reports.fullFile' as const;
const K_R_RACES = 'reports.races' as const;
const K_R_PREVIEW = 'reports.preview' as const;
const K_R_PREVIEW_HINT = 'reports.previewHint' as const;
const K_R_PREVIEW_LOADING = 'reports.previewLoading' as const;
const K_R_PREVIEW_REPORT = 'reports.previewReport' as const;
const K_R_CLOSE_PREVIEW = 'reports.closePreview' as const;

interface RawRace {
  report_id?: unknown;
  id?: unknown;
  file_data_segment_id?: unknown;
  race_name?: unknown;
  name?: unknown;
}

interface RawReport {
  event?: unknown;
  boat?: unknown;
  n_races?: unknown;
  races?: unknown;
  event_id?: unknown;
  file_id?: unknown;
  file_date?: unknown;
  is_true?: unknown;
  is_report?: unknown;
  sharing?: unknown;
  display_name?: unknown;
}

interface ApiGetReportsResponse {
  success: boolean;
  message?: string;
  data: RawReport[];
}

function isString(v: unknown): v is string {
  return typeof v === 'string';
}
function isNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}
function toNumberString(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number') {
    return String(value);
  }
  return String(value as string);
}
function parseDateISO(value: unknown, t: (k: string) => string): string {
  if (isString(value) || isNumber(value)) {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? t('reports.dateNotAvailable') : d.toISOString().split('T')[0];
  }
  return t('reports.dateNotAvailable');
}
function isRawRaceArray(v: unknown): v is RawRace[] {
  return Array.isArray(v);
}

const isOwnedSharing = (sharing?: SharingMetadata | null): boolean => sharing?.isOwner ?? true;

const parseSharedUser = (value: unknown): SharedUser | null => {
  if (value && typeof value === 'object') {
    const raw = value as Record<string, unknown>;
    const idValue = raw.id;
    const id =
      typeof idValue === 'number'
        ? idValue
        : typeof idValue === 'string'
          ? Number.parseInt(idValue, 10)
          : Number.NaN;
    if (Number.isFinite(id)) {
      const emailRaw = raw.email;
      const email =
        typeof emailRaw === 'string'
          ? emailRaw
          : emailRaw === undefined
            ? ''
            : String(emailRaw);
      const fullName =
        typeof raw.full_name === 'string'
          ? raw.full_name
          : typeof raw.fullName === 'string'
            ? raw.fullName
            : null;
      return { id, email, full_name: fullName };
    }
  }
  return null;
};

const parseSharing = (value: unknown): SharingMetadata | undefined => {
  if (value && typeof value === 'object') {
    const v = value as Record<string, unknown>;
    const hasFlags =
      typeof v.isOwner === 'boolean' &&
      typeof v.sharedByMe === 'boolean' &&
      typeof v.sharedWithMe === 'boolean' &&
      typeof v.isShared === 'boolean';
    if (!hasFlags) {
      return undefined;
    }
    const sharedWith =
      Array.isArray(v.sharedWith) && v.sharedWith.length > 0
        ? (v.sharedWith
            .map((candidate) => parseSharedUser(candidate))
            .filter(Boolean) as SharedUser[])
        : undefined;
    const owner = parseSharedUser(v.owner);
    return {
      isOwner: v.isOwner as boolean,
      sharedByMe: v.sharedByMe as boolean,
      sharedWithMe: v.sharedWithMe as boolean,
      isShared: v.isShared as boolean,
      sharedWith,
      owner: owner ?? undefined,
    };
  }
  return undefined;
};

function formatReports(
  data: RawReport[],
  t: (k: string) => string,
): Array<{ report: Report; fileId: string }> {
  const formattedReports: Array<{ report: Report; fileId: string }> = [];

  for (const r of data) {
    const date = parseDateISO(r.file_date, t);
    const event = isString(r.event) ? r.event : t('reports.unknownEvent');
    const boat = isString(r.boat) ? r.boat : t('reports.unknownBoat');
    const eventId = isNumber(r.event_id)
      ? r.event_id
      : Number.parseInt(String(r.event_id ?? -1), 10);
    const fileIdString = toNumberString(r.file_id);
    const isFull = r.is_true === true || r.is_report === true;
    const sharing = parseSharing((r as { sharing?: unknown }).sharing);

    const racesArray: RawRace[] = isRawRaceArray(r.races) ? r.races : [];
    const racesCount = isNumber(r.n_races)
      ? r.n_races
      : Array.isArray(racesArray)
        ? racesArray.length
        : 0;

    if (isFull) {
      formattedReports.push({
        report: {
          event,
          date,
          boat,
          racesCount,
          eventId,
          raceIds: [],
          isFullFile: true,
          sharing,
        },
        fileId: fileIdString,
      });
    }

    if (racesArray.length > 0) {
      let index = 0;
      for (const race of racesArray) {
        index += 1;
        const reportIdUnknown = race.report_id ?? race.id ?? race.file_data_segment_id;
        const reportId = Number.parseInt(String(reportIdUnknown), 10);
        if (!Number.isNaN(reportId)) {
          const fallbackName = `${t('reports.race')} ${index}`;
          const raceNameCandidate = race.race_name ?? race.name;
          const raceName = isString(raceNameCandidate) ? raceNameCandidate : fallbackName;
          formattedReports.push({
            report: {
              event,
              date,
              boat,
              racesCount: 1,
              eventId,
              raceIds: [reportId],
              raceName,
              sharing,
            },
            fileId: fileIdString,
          });
        }
      }
    }
  }

  return formattedReports;
}

const buildReportKey = (fileId: string, raceIds: number[], eventId: number): string =>
  `${fileId}-${raceIds.join('_')}-${eventId}`;

interface Report {
  event: string;
  date: string;
  boat: string;
  racesCount: number;
  eventId: number;
  raceIds: number[];
  isFullFile?: boolean;
  raceName?: string;
  sharing?: SharingMetadata;
}

interface ReportPreviewState {
  url: string;
  report: Report;
  fileId: string;
  downloadKey: string;
}

interface ReportRequestConfig {
  token: string;
  numericFileId: number;
  raceId?: number;
}

function handleDateSelect(_eventId: string, _date: string): void {
  // Selection handled by modal UI
}

const ReportsPage: React.FC = () => {
  const { t } = useTranslation('common');
  const [reports, setReports] = useState<Array<{ report: Report; fileId: string }>>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState<string>(SORT_DATE_DESC);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isFileModalVisible, setIsFileModalVisible] = useState(false); // Nuevo estado para el modal de selección de archivos
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  // Tracks the fileId + raceIds for the report we want to delete
  const [selectedDeleteReport, setSelectedDeleteReport] = useState<{
    fileId: string;
    raceIds: number[];
  } | null>(null);

  // Nuevo estado para manejar descargas múltiples
  const [downloadingCards, setDownloadingCards] = useState<Set<string>>(new Set());

  const [isModalActionsVisible, setIsModalActionsVisible] = useState(false);
  const [previewState, setPreviewState] = useState<ReportPreviewState | null>(null);
  const [previewLoadingKey, setPreviewLoadingKey] = useState<string | null>(null);
  const previewContainerRef = useRef<HTMLDivElement | null>(null);
  const sortDropdownRef = useRef<HTMLDivElement | null>(null);
  const sortButtonRef = useRef<HTMLButtonElement | null>(null);
  const [isSingleColumnLayout, setIsSingleColumnLayout] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia('(max-width: 1280px)').matches;
  });

  const handleCloseModalActions = () => {
    setIsModalActionsVisible(false);
  };

  const actionsDeleteReport: ModalAction[] = [
    {
      label: t('reports.actions.cancel'),
      onClick: handleCloseModalActions,
      styleType: 'secondary',
    },
    {
      label: t('reports.actions.accept'),
      onClick: () => {
        if (selectedDeleteReport) {
          void deleteReport(selectedDeleteReport.fileId, selectedDeleteReport.raceIds);
        }
        handleCloseModalActions();
      },
      styleType: 'primary',
    },
  ];

  const fetchReports = useCallback(async (): Promise<void> => {
    try {
      if (!API_BASE_URL) {
        showMessage('error', t(K_NOTIF_ERROR), t(K_R_BACKEND));
        return;
      }
      const token = localStorage.getItem('token');
      if (!token) {
        showMessage('error', t(K_NOTIF_ERROR), t(K_R_AUTH));
        return;
      }
      const url = `${API_BASE_URL}/api/user/getReports`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || t(K_R_FETCH_FAIL));
      }

      const json: unknown = await response.json();
      const data = json as Partial<ApiGetReportsResponse>;
      if (data?.success !== true || !Array.isArray(data?.data)) {
        throw new Error((data?.message && String(data.message)) || t(K_R_FETCH_FAIL));
      }

      const formattedReports = formatReports(data.data, t);
      setReports(formattedReports);
    } catch (error) {
      showMessage('error', t(K_NOTIF_ERROR), (error as Error).message || t(K_R_FETCH_FAIL));
    }
  }, [t]);

  useEffect(() => {
    void fetchReports();
  }, [fetchReports]);

  useEffect(() => {
    if (!isDropdownVisible) {
      return;
    }

    const closeSortDropdown = () => {
      setIsDropdownVisible(false);
    };

    const handlePointerDown = (event: Event) => {
      const targetNode = event.target as Node | null;
      if (
        targetNode &&
        sortDropdownRef.current &&
        !sortDropdownRef.current.contains(targetNode)
      ) {
        closeSortDropdown();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeSortDropdown();
        sortButtonRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDropdownVisible]);

  useEffect(() => {
    const onRefresh = (): void => {
      void fetchReports();
    };
    window.addEventListener('reports:refresh', onRefresh as EventListener);
    return () => window.removeEventListener('reports:refresh', onRefresh as EventListener);
  }, [fetchReports]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }

    const mediaQuery = window.matchMedia('(max-width: 1280px)');
    const handleChange = (event: MediaQueryListEvent) => {
      setIsSingleColumnLayout(event.matches);
    };

    let cleanup: (() => void) | undefined;
    setIsSingleColumnLayout(mediaQuery.matches);
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      cleanup = () => {
        mediaQuery.removeEventListener('change', handleChange);
      };
    } else if (typeof mediaQuery.addListener === 'function') {
      const legacyListener = (event: MediaQueryListEvent) => handleChange(event);
      mediaQuery.addListener(legacyListener);
      cleanup = () => {
        mediaQuery.removeListener(legacyListener);
      };
    }

    return () => {
      cleanup?.();
    };
  }, []);

  const handleFileUpload = () => {
    setIsModalVisible(true);
  };

  const handleFileSelectModal = () => {
    setIsFileModalVisible(true);
  };

  const getReportRequestConfig = (
    fileId: string,
    raceIds: number[],
    eventId: number,
  ): ReportRequestConfig | null => {
    if (!API_BASE_URL) {
      showMessage('error', t(K_NOTIF_ERROR), t(K_R_BACKEND));
      return null;
    }
    if (!fileId) {
      showMessage('error', t(K_NOTIF_ERROR), t(K_R_NO_FILEID));
      return null;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      showMessage('error', t(K_NOTIF_ERROR), t(K_R_AUTH));
      return null;
    }

    const numericFileId = Number.parseInt(fileId, 10);
    if (Number.isNaN(numericFileId)) {
      showMessage('error', t(K_NOTIF_ERROR), t(K_R_INVALID_FILE));
      return null;
    }
    if (!Number.isInteger(eventId)) {
      showMessage('error', t(K_NOTIF_ERROR), t(K_R_INVALID_EVENT));
      return null;
    }

    const raceId =
      Array.isArray(raceIds) && raceIds.length === 1 ? raceIds[0] : undefined;

    return { token, numericFileId, raceId };
  };

  const requestReportBlob = async (
    token: string,
    numericFileId: number,
    eventId: number,
    raceId?: number,
  ): Promise<Blob> => {
    const resp = await fetch(`${API_BASE_URL}/api/user/downloadReport`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        fileId: numericFileId,
        eventId,
        ...(raceId === undefined ? {} : { raceId }),
      }),
    });

    if (!resp.ok) {
      let message = t(K_R_DOWNLOAD_FAIL);
      try {
        const index: unknown = await resp.json();
        if (index && typeof index === 'object') {
          const payload = index as Record<string, unknown>;
          const maybeMessage = payload.message;
          if (typeof maybeMessage === 'string') {
            message = maybeMessage;
          }
        }
      } catch {
        // ignore parsing failure, fallback message will be used
      }
      throw new Error(message);
    }

    return resp.blob();
  };

  const closePreview = useCallback(() => {
    setPreviewState((previous) => {
      if (previous?.url) {
        window.URL.revokeObjectURL(previous.url);
      }
      return null;
    });
  }, []);

  const openPreview = async (report: Report, fileId: string): Promise<void> => {
    const { eventId, raceIds } = report;
    const downloadKey = buildReportKey(fileId, raceIds, eventId);
    if (previewLoadingKey === downloadKey) {
      return;
    }

    const config = getReportRequestConfig(fileId, raceIds, eventId);
    if (!config) {
      return;
    }

    const { token, numericFileId, raceId } = config;
    setPreviewLoadingKey(downloadKey);
    try {
      const blob = await requestReportBlob(token, numericFileId, eventId, raceId);
      const url = window.URL.createObjectURL(blob);
      setPreviewState((previous) => {
        if (previous?.url) {
          window.URL.revokeObjectURL(previous.url);
        }
        return { url, report, fileId, downloadKey };
      });
    } catch (error) {
      const message =
        error instanceof Error && error.message ? error.message : t(K_R_DOWNLOAD_FILE_FAIL);
      showMessage('error', t(K_NOTIF_ERROR), message);
    } finally {
      setPreviewLoadingKey((current) => (current === downloadKey ? null : current));
    }
  };

  useEffect(() => {
    if (!previewState) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closePreview();
      }
    };

    const handlePointerDown = (event: Event) => {
      const targetNode = event.target as Node | null;
      if (
        previewContainerRef.current &&
        targetNode &&
        !previewContainerRef.current.contains(targetNode)
      ) {
        closePreview();
      }
    };

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [previewState, closePreview]);

  useEffect(() => {
    return () => {
      if (previewState?.url) {
        window.URL.revokeObjectURL(previewState.url);
      }
    };
  }, [previewState]);
  const downloadReport = async (
    fileId: string,
    raceIds: number[],
    eventId: number,
  ): Promise<void> => {
    const config = getReportRequestConfig(fileId, raceIds, eventId);
    if (!config) {
      return;
    }

    const { token, numericFileId, raceId } = config;
    const downloadKey = buildReportKey(fileId, raceIds, eventId);
    if (downloadingCards.has(downloadKey)) {
      return;
    }

    setDownloadingCards((previous) => new Set(previous).add(downloadKey));
    try {
      const blob = await requestReportBlob(token, numericFileId, eventId, raceId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const baseName =
        raceId === undefined ? `${numericFileId}.pdf` : `${numericFileId}_${raceId}.pdf`;
      a.href = url;
      a.download = baseName;
      document.body.append(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      showMessage('success', t('notifications.success'), t('reports.downloadSuccess'));
    } catch (error) {
      showMessage('error', t(K_NOTIF_ERROR), (error as Error).message || t(K_R_DOWNLOAD_FILE_FAIL));
    } finally {
      setDownloadingCards((previous) => {
        const s = new Set(previous);
        s.delete(downloadKey);
        return s;
      });
    }
  };

  const deleteReport = async (fileId: string, raceIds: number[]): Promise<void> => {
    if (!API_BASE_URL) {
      return showMessage('error', t(K_NOTIF_ERROR), t('reports.backendUrlMissing'));
    }
    if (!fileId) {
      return showMessage('error', t(K_NOTIF_ERROR), t('reports.noFileId'));
    }

    const token = localStorage.getItem('token');
    if (!token) {
      return showMessage('error', t(K_NOTIF_ERROR), t('reports.authError'));
    }

    const numericFileId = Number.parseInt(fileId, 10);
    if (Number.isNaN(numericFileId)) {
      return showMessage('error', t(K_NOTIF_ERROR), t('reports.invalidFileId'));
    }

    const targetReport = reports.find(({ fileId: fid }) => fid === String(numericFileId));
    if (targetReport && !isOwnedSharing(targetReport.report.sharing)) {
      return showMessage(
        'error',
        t(K_NOTIF_ERROR),
        t('sharing.onlyMineActions', 'Solo puedes modificar tus propios informes'),
      );
    }

    try {
      const resp = await fetch(`${API_BASE_URL}/api/user/deleteReport`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileId: numericFileId,
          ...(Array.isArray(raceIds) && raceIds.length > 0 ? { raceIds } : {}),
        }),
      });

      const index: unknown = await resp.json();
      const payload = index && typeof index === 'object' ? (index as Record<string, unknown>) : {};
      if (!resp.ok) {
        const message = typeof payload.message === 'string' ? payload.message : t('reports.deleteFail');
        throw new Error(message);
      }

      setReports((previous) => {
        if (Array.isArray(raceIds) && raceIds.length > 0) {
          const toDeleteSet = new Set(raceIds);
          return previous.filter(({ fileId: fid, report }) => {
            const sameFile = fid === String(numericFileId);
            const isSingleRaceToDelete =
              Array.isArray(report.raceIds) &&
              report.raceIds.length === 1 &&
              toDeleteSet.has(report.raceIds[0]);
            return !sameFile || !isSingleRaceToDelete;
          });
        }
        return previous.filter(({ fileId: fid, report }) => {
          const sameFile = fid === String(numericFileId);
          const isFull =
            report.isFullFile === true ||
            (Array.isArray(report.raceIds) && report.raceIds.length === 0);
          return !sameFile || !isFull;
        });
      });

      showMessage('success', t('notifications.success'), t('reports.deleteSuccess'));
    } catch {
      showMessage('error', t(K_NOTIF_ERROR), t('reports.deleteReportFail'));
    }
  };

  const EmptyReportsState = () => (
    <div className="empty-reports-container">
      <div className="empty-reports-icon" aria-hidden="true">
        <ClipboardReportsIcon focusable="false" />
      </div>
      <h3 className="empty-reports-title">{t('reports.empty.title')}</h3>
      <p className="empty-reports-message">{t('reports.empty.message')}</p>
      <div className="empty-reports-suggestion">{t('reports.empty.suggestion')}</div>
    </div>
  );

  // 2) Componente completo ReportCard con raceIds extraído de report

  interface ReportCardProps {
    report: Report;
    fileId: string;
    onPreview: () => void;
    isPreviewLoading: boolean;
    isPreviewActive: boolean;
    isSingleColumn: boolean;
  }

  const ReportCard: React.FC<ReportCardProps> = ({
    report,
    fileId,
    onPreview,
    isPreviewLoading,
    isPreviewActive,
    isSingleColumn,
  }) => {
    const { eventId, raceIds, event, date, boat, racesCount, isFullFile, raceName } = report;
    const downloadKey = buildReportKey(fileId, raceIds, eventId);
    const isDownloading = downloadingCards.has(downloadKey);
    const disablePreview = isDownloading || isPreviewLoading;
    const canManage = isOwnedSharing(report.sharing);

    const previewLoadingText = t(K_R_PREVIEW_LOADING, 'Loading preview...');
    const previewLabel = isPreviewLoading ? previewLoadingText : t(K_R_PREVIEW, 'Preview');
    const previewHint = isPreviewLoading ? previewLoadingText : t(K_R_PREVIEW_HINT, 'Open PDF');
    const reportScopeLabel = isFullFile
      ? `${racesCount} ${t(K_R_RACES)}`
      : Array.isArray(raceIds) && raceIds.length > 1
        ? `${raceIds.length} ${t(K_R_RACES)}`
        : raceName;
    const reportDateLabel = `${date}${
      isFullFile ? ' · ' + t(K_R_FULL_FILE, 'Full File') : raceName ? ` · ${raceName}` : ''
    }`;

    const imageClasses = ['report-image'];
    if (isPreviewLoading) {
      imageClasses.push('preview-loading');
    }
    if (isPreviewActive) {
      imageClasses.push('preview-active');
    }
    const isSharedWithMe = Boolean(report.sharing?.sharedWithMe);
    const cardClasses = ['report-card'];
    if (isSingleColumn) {
      cardClasses.push('single-column');
    }
    if (isDownloading) {
      cardClasses.push('downloading');
    }
    if (isSharedWithMe) {
      cardClasses.push('shared-with-me-border');
    }

    return (
      <div
        className={cardClasses.join(' ')}
        data-download-key={downloadKey}
      >
        {isDownloading && (
          <>
            <div className="download-progress-overlay" />
            <div className="download-status-badge">{t('reports.downloading', 'Downloading...')}</div>
          </>
        )}
        <button
          type="button"
          className={imageClasses.join(' ')}
          onClick={onPreview}
          aria-label={t(K_R_PREVIEW_REPORT, 'Preview report')}
          aria-busy={isPreviewLoading}
          title={previewLabel}
          data-preview-key={downloadKey}
          disabled={disablePreview}
        >
          <div className="report-image-content">
            {isPreviewLoading ? (
              <>
                <div className="report-image-loader" aria-hidden="true" />
                <span className="report-image-helper">{previewHint}</span>
              </>
            ) : (
              <>
                <span className="report-image-label">{previewLabel}</span>
                <span className="report-image-helper">{previewHint}</span>
              </>
            )}
          </div>
        </button>
        <div className="report-content">
          <div className="report-header">
            <h3>{event}</h3>
            <div className="report-actions">
              <button
                type="button"
                className={`download-button ${isDownloading ? 'downloading' : ''}`}
                onClick={() => {
                  void downloadReport(fileId, raceIds, eventId);
                }}
                disabled={isDownloading}
                aria-label={t('reports.downloadReport', 'Download report')}
                aria-busy={isDownloading}
                title={t('reports.downloadReport', 'Download report')}
              >
                <DownloadFileIcon className="download-icon" aria-hidden="true" focusable="false" />
              </button>
              {canManage && (
                <button
                  type="button"
                  className="delete-button"
                  onClick={() => {
                    setSelectedDeleteReport({ fileId, raceIds });
                    setIsModalActionsVisible(true);
                  }}
                  aria-label={t('reports.deleteReport', 'Delete report')}
                  title={t('reports.deleteReport', 'Delete report')}
                >
                  <TrashIcon className="trash-icon" aria-hidden="true" focusable="false" />
                </button>
              )}
            </div>
          </div>
          <div className="report-details">
            <p className="report-boat">{boat}</p>
            {reportScopeLabel && <p className="report-scope">{reportScopeLabel}</p>}
            <p className="report-date">{reportDateLabel}</p>
          </div>
        </div>
      </div>
    );
  };

  const handleModalClose = () => {
    //window.location.reload();
    setIsModalVisible(false);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleSortChange = (option: string) => {
    setSortOption(option);
    setIsDropdownVisible(false);
    sortButtonRef.current?.focus();

    const sortedReports = [...reports];

    switch (option) {
      case SORT_DATE_ASC: {
        sortedReports.sort(
          (a, b) => new Date(a.report.date).getTime() - new Date(b.report.date).getTime(),
        );
        break;
      }
      case SORT_DATE_DESC: {
        sortedReports.sort(
          (a, b) => new Date(b.report.date).getTime() - new Date(a.report.date).getTime(),
        );
        break;
      }
      case SORT_EVENT_AZ: {
        sortedReports.sort((a, b) => a.report.event.localeCompare(b.report.event));
        break;
      }
      case SORT_EVENT_ZA: {
        sortedReports.sort((a, b) => b.report.event.localeCompare(a.report.event));
        break;
      }
      case SORT_BOAT_AZ: {
        sortedReports.sort((a, b) => a.report.boat.localeCompare(b.report.boat));
        break;
      }
      case SORT_BOAT_ZA: {
        sortedReports.sort((a, b) => b.report.boat.localeCompare(a.report.boat));
        break;
      }
      default: {
        break;
      }
    }

    setReports(sortedReports);
  };

  const filteredReports = reports.filter(({ report }) =>
    report.event.toLowerCase().includes(searchTerm.toLowerCase()),
  );
  const shouldShowInlineActions = isSingleColumnLayout || filteredReports.length <= 1;

  const previewDialogLabel = t(K_R_PREVIEW_REPORT, 'Preview report');
  const closePreviewLabel = t(K_R_CLOSE_PREVIEW, 'Close preview');
  const searchReportsLabel = t('reports.searchReports', 'Search reports');
  const sortReportsLabel = t('reports.sort.label', 'Sort reports');
  const resultStatusText =
    searchTerm.trim().length > 0
      ? t('reports.results.filtered', {
          count: filteredReports.length,
          defaultValue: `${filteredReports.length} of ${reports.length} reports`,
          total: reports.length,
        })
      : t('reports.results.total', {
          count: reports.length,
          defaultValue: `${reports.length} reports`,
        });
  const previewSecondaryLabel =
    previewState?.report.isFullFile === true
      ? t(K_R_FULL_FILE, 'Full File')
      : previewState?.report
        ? previewState.report.raceName ?? `${previewState.report.raceIds.length} ${t(K_R_RACES)}`
        : '';

  return (
    <div className="report-generator-container">
      <div className="report-generator-header">
        <Link to="/home" className="logo-link" aria-label={t('navigation.home', 'Home')}>
          <SailTrackLogo className="logo-race" role="img" aria-label="SailTrack Logo" focusable="false" />
        </Link>
        <h1>{t('reports.title')}</h1>
      </div>
      <div className="report-container">
        <div className="header">
          <div className="actions">
            <h2>{t('reports.last20', 'Last 20 Generated Reports')}</h2>
            <div className="search-sort-container">
              <div className="search-container">
                <SearchIcon className="search-icon" aria-hidden="true" focusable="false" />
                <input
                  type="text"
                  placeholder={t('common.search') + '...'}
                  value={searchTerm}
                  onChange={handleSearch}
                  className="search-input"
                  aria-label={searchReportsLabel}
                />
              </div>
              <div className="sort-dropdown" ref={sortDropdownRef}>
                <button
                  ref={sortButtonRef}
                  type="button"
                  className="sort-button"
                  onClick={() => setIsDropdownVisible(!isDropdownVisible)}
                  aria-label={sortReportsLabel}
                  aria-haspopup="menu"
                  aria-expanded={isDropdownVisible}
                  aria-controls={isDropdownVisible ? 'reports-sort-menu' : undefined}
                >
                  <SortReportsIcon className="sort-icon" aria-hidden="true" focusable="false" />
                </button>
                {isDropdownVisible && (
                  <div id="reports-sort-menu" className="dropdown-content" role="menu" aria-label={sortReportsLabel}>
                    <button
                      type="button"
                      className={sortOption === SORT_DATE_ASC ? 'selected' : ''}
                      onClick={() => handleSortChange(SORT_DATE_ASC)}
                      role="menuitemradio"
                      aria-checked={sortOption === SORT_DATE_ASC}
                    >
                      {t('reports.sort.dateAsc')}
                    </button>
                    <button
                      type="button"
                      className={sortOption === SORT_DATE_DESC ? 'selected' : ''}
                      onClick={() => handleSortChange(SORT_DATE_DESC)}
                      role="menuitemradio"
                      aria-checked={sortOption === SORT_DATE_DESC}
                    >
                      {t('reports.sort.dateDesc')}
                    </button>
                    <button
                      type="button"
                      className={sortOption === SORT_EVENT_AZ ? 'selected' : ''}
                      onClick={() => handleSortChange(SORT_EVENT_AZ)}
                      role="menuitemradio"
                      aria-checked={sortOption === SORT_EVENT_AZ}
                    >
                      {t('reports.sort.eventAZ')}
                    </button>
                    <button
                      type="button"
                      className={sortOption === SORT_EVENT_ZA ? 'selected' : ''}
                      onClick={() => handleSortChange(SORT_EVENT_ZA)}
                      role="menuitemradio"
                      aria-checked={sortOption === SORT_EVENT_ZA}
                    >
                      {t('reports.sort.eventZA')}
                    </button>
                    <button
                      type="button"
                      className={sortOption === SORT_BOAT_AZ ? 'selected' : ''}
                      onClick={() => handleSortChange(SORT_BOAT_AZ)}
                      role="menuitemradio"
                      aria-checked={sortOption === SORT_BOAT_AZ}
                    >
                      {t('reports.sort.boatAZ')}
                    </button>
                    <button
                      type="button"
                      className={sortOption === SORT_BOAT_ZA ? 'selected' : ''}
                      onClick={() => handleSortChange(SORT_BOAT_ZA)}
                      role="menuitemradio"
                      aria-checked={sortOption === SORT_BOAT_ZA}
                    >
                      {t('reports.sort.boatZA')}
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="right-actions">
              <button type="button" className="button" onClick={handleFileSelectModal}>
                {t('reports.selectFile')}
                <SelectCSVFileIcon aria-hidden="true" focusable="false" />
              </button>
              <button type="button" className="upload-button-report" onClick={handleFileUpload}>
                {t('reports.uploadFile')}
                <UploadFileIcon aria-hidden="true" focusable="false" />
              </button>
            </div>
          </div>
        </div>
        <p className="reports-status" aria-live="polite">{resultStatusText}</p>
        {reports.length === 0 ? (
          <EmptyReportsState />
        ) : filteredReports.length === 0 ? (
          <div className="empty-reports-container">
            <div className="empty-reports-icon" aria-hidden="true">
              <SearchIcon focusable="false" />
            </div>
            <h3 className="empty-reports-title">{t('reports.emptyFiltered.title')}</h3>
            <p className="empty-reports-message">{t('reports.emptyFiltered.message')}</p>
            <div className="empty-reports-suggestion">{t('reports.emptyFiltered.suggestion')}</div>
          </div>
        ) : (
          <div className="report-grid">
            {filteredReports.map(({ report, fileId }, index) => {
              const cardKey = buildReportKey(fileId, report.raceIds, report.eventId);
              return (
                <ReportCard
                  key={`${cardKey}-${index}`}
                  report={report}
                  fileId={fileId}
                  onPreview={() => {
                    void openPreview(report, fileId);
                  }}
                  isPreviewLoading={previewLoadingKey === cardKey}
                  isPreviewActive={previewState?.downloadKey === cardKey}
                  isSingleColumn={shouldShowInlineActions}
                />
              );
            })}
          </div>
        )}
      </div>
      {previewState && (
        <div className="report-preview-backdrop">
          <div
            className="report-preview-container"
            ref={previewContainerRef}
            role="dialog"
            aria-modal="true"
            aria-label={previewDialogLabel}
          >
            <button
              type="button"
              className="report-preview-close"
              onClick={closePreview}
              aria-label={closePreviewLabel}
            >
              <PlusIcon className="report-preview-close-icon" />
            </button>
            <div className="report-preview-frame">
              <iframe
                src={previewState.url}
                title={previewDialogLabel}
              />
            </div>
            <div className="report-preview-meta">
              <div className="report-preview-meta-primary">
                <h3>{previewState.report.event}</h3>
                <p>{previewState.report.boat}</p>
              </div>
              <div className="report-preview-meta-secondary">
                <span>{previewState.report.date}</span>
                <span>{previewSecondaryLabel}</span>
              </div>
            </div>
          </div>
        </div>
      )}
      <ActionsModal
        isVisible={isModalActionsVisible}
        title={t('reports.confirmDeleteTitle')}
        message={t('reports.confirmDeleteMessage')}
        actions={actionsDeleteReport}
        onClose={handleCloseModalActions}
      />
      {isModalVisible && (
        <ModalPortal>
          <ModalReports isVisible={isModalVisible} onClose={handleModalClose} userId={''} />
        </ModalPortal>
      )}

      {isFileModalVisible && (
        <ModalPortal>
          <ModalSelect
            isVisible={isFileModalVisible}
            onClose={() => setIsFileModalVisible(false)}
            onDateSelect={handleDateSelect}
            onReportGenerated={fetchReports} // Aquí se pasa el callback
          />
        </ModalPortal>
      )}
    </div>
  );
};

export { ReportsPage };
